/**
 * ProductGroup JSON-LD for products grouped by the Grouptify app.
 *
 * On this store a "variant" is a separate product with its own URL, so Google's multi-page
 * variant shape applies: every member page carries the whole ProductGroup and points at each
 * sibling by URL. The grouping is only available at runtime, as window.mpg.group — see
 * snippets/structured-data-product-group.liquid for why it cannot be read in Liquid.
 *
 * The current product's entry reuses the @id of the Product node already in <head>, so the two
 * describe one entity instead of competing.
 */
(() => {
  const CONFIG_SELECTOR = '[data-product-group-config]';
  // JSON.stringify leaves "<" raw, so a feed title containing </script> would close this
  // tag and spill the rest of the node into the page. '\u005Cu003c' is the two-character
  // sequence \u003c, which JSON readers decode back to "<".
  const ESCAPED_LT = '\u005Cu003c';
  // The observer is what actually drives injection; the interval only covers the case where the
  // app publishes its global without touching the DOM. The deadline exists so neither outlives
  // the page.
  const FALLBACK_POLL_INTERVAL = 250;
  const WAIT_DEADLINE = 30000;

  // Google accepts these as variesBy; an axis it has no vocabulary for (this store's "Type")
  // is still rendered as a variant, it just cannot be named as the thing that varies.
  const VARIES_BY = {
    color: 'https://schema.org/color',
    size: 'https://schema.org/size',
    material: 'https://schema.org/material',
    pattern: 'https://schema.org/pattern'
  };

  const readConfig = () => {
    const node = document.querySelector(CONFIG_SELECTOR);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[product-group] config is not valid JSON', error);
      return null;
    }
  };

  // Path-relative on purpose: it has to match the @id Judge.me and the Liquid Product
  // node already use, or the three describe different products.
  const productId = (path) => `${path}#product`;

  const optionValueNames = (group) => {
    const names = new Map();
    (group.options || []).forEach((option) => {
      (option.values || []).forEach((value) => names.set(value.id, { axis: option.name, name: value.name }));
    });
    return names;
  };

  const buildVariant = (config, group, names, member) => {
    const path = `/products/${member.handle}`;
    const variant = {
      '@type': 'Product',
      '@id': productId(path),
      name: member.title,
      url: config.origin + path,
      inProductGroupWithID: group.id,
      offers: {
        '@type': 'Offer',
        url: config.origin + path,
        price: member.sale_price,
        priceCurrency: config.currency,
        itemCondition: 'https://schema.org/NewCondition',
        // Frog drives availability, and Liquid only knows it for the page we are on. A sibling
        // falls back to what the app carries, which is Shopify's own flag.
        availability:
          member.handle === config.currentHandle
            ? `https://schema.org/${config.currentAvailability}`
            : `https://schema.org/${member.available ? 'InStock' : 'OutOfStock'}`
      }
    };

    if (member.images && member.images.length) variant.image = member.images[0];

    (member.options || []).forEach((valueId) => {
      const value = names.get(valueId);
      if (!value) return;
      const axis = value.axis.toLowerCase();
      if (VARIES_BY[axis]) variant[axis] = value.name;
    });

    return variant;
  };

  const inject = (config, group) => {
    const members = (group.products || []).filter((member) => member.status === 'ACTIVE');
    if (members.length < 2) return;

    const names = optionValueNames(group);
    const variesBy = (group.options || [])
      .map((option) => VARIES_BY[option.name.toLowerCase()])
      .filter(Boolean);

    const node = {
      '@context': 'https://schema.org',
      '@type': 'ProductGroup',
      name: config.name,
      productGroupID: group.id,
      hasVariant: members.map((member) => buildVariant(config, group, names, member))
    };
    if (variesBy.length) node.variesBy = variesBy;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.productGroup = '';
    script.textContent = JSON.stringify(node).replace(/</g, ESCAPED_LT);
    document.head.appendChild(script);
  };

  const readGroup = () => {
    const group = window.mpg && window.mpg.group;
    return group && Array.isArray(group.products) && group.products.length ? group : null;
  };

  // Grouptify publishes window.mpg.group as it renders its swatches, so a DOM mutation is the
  // signal that it has run — arrival drives injection instead of a fixed window.
  const waitForGroup = (onGroup) => {
    const ready = readGroup();
    if (ready) {
      onGroup(ready);
      return;
    }

    let settled = false;
    const stop = (group) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearInterval(poll);
      window.clearTimeout(deadline);
      if (group) {
        onGroup(group);
      } else {
        console.warn(
          '[product-group] window.mpg.group never arrived; no ProductGroup markup on this page'
        );
      }
    };
    const check = () => {
      const group = readGroup();
      if (group) stop(group);
    };

    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    const poll = window.setInterval(check, FALLBACK_POLL_INTERVAL);
    const deadline = window.setTimeout(() => stop(null), WAIT_DEADLINE);
  };

  const start = () => {
    const config = readConfig();
    if (!config) return;
    if (document.querySelector('script[data-product-group]')) return;

    waitForGroup((group) => inject(config, group));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
